import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiActivity, FiTarget, FiAlertCircle, FiTrendingUp, FiUserCheck, FiZap } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import api from '../lib/api';

export default function RWEDashboard() {
  const [data, setData] = useState([]);
  const [metrics, setMetrics] = useState({
    accuracy: 0,
    overrideRate: 0,
    outcomeImpact: 0,
    activePatients: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: stats } = await api.get('/compliance/rwe-stats');
        setMetrics({
          accuracy: stats.accuracy,
          overrideRate: stats.overrideRate,
          outcomeImpact: stats.outcomeImpact,
          activePatients: stats.activePatients
        });
        setData(stats.trend || []);
      } catch (err) {
        console.error("Failed to fetch RWE stats");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500">Loading Clinical Evidence...</div>;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard label="Model Accuracy" value={`${metrics.accuracy.toFixed(1)}%`} trend="+0.4%" icon={FiTarget} color="emerald" />
        <StatCard label="Override Rate" value={`${metrics.overrideRate.toFixed(1)}%`} trend="-0.2%" icon={FiAlertCircle} color="amber" />
        <StatCard label="Outcome Impact" value={`${metrics.outcomeImpact}%`} trend="+5.1%" icon={FiTrendingUp} color="primary" />
        <StatCard label="Monitored Population" value={metrics.activePatients} trend="+12%" icon={FiUserCheck} color="indigo" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-[2.5rem]">
          <h3 className="text-lg font-black mb-6 flex items-center gap-2">
            <SafeIcon icon={FiActivity} className="text-primary-500" /> Performance Stability (12M)
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorAcc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007bff" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#007bff" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={10} axisLine={false} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={10} axisLine={false} tickLine={false} domain={[80, 100]} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px' }}
                  itemStyle={{ color: '#fff', fontSize: '12px' }}
                />
                <Area type="monotone" dataKey="accuracy" stroke="#007bff" strokeWidth={3} fillOpacity={1} fill="url(#colorAcc)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-[2.5rem]">
          <h3 className="text-lg font-black mb-6 flex items-center gap-2 text-amber-500">
            <SafeIcon icon={FiZap} /> Clinical Override Impact
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={10} axisLine={false} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={10} axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px' }}
                />
                <Line type="monotone" dataKey="overrides" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, trend, icon, color }) {
  const colors = { emerald: 'text-emerald-500', amber: 'text-amber-500', primary: 'text-primary-500', indigo: 'text-indigo-500' };
  return (
    <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl group hover:border-slate-700 transition-all">
      <div className="flex justify-between items-start mb-4">
        <div className={`w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <SafeIcon icon={icon} />
        </div>
        <span className="text-[10px] font-black text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded-full">{trend}</span>
      </div>
      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-2xl font-black text-white">{value}</p>
    </div>
  );
}